import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button, Card, FlexGap, Heading3, Paragraph, tokens } from '@treams/treams-ui';

import ConversationParty from '../ConversationParty';

export default function EvaluationSubmissionUnsign(props: {
	submission: graphql.EvaluationSubmission;
}) {
	const roles = useViewerRoles();
	const { t } = useTranslation();
	const [loadingType, setLoadingType] = useState<'employee' | 'manager'>('employee');

	const [unsign, { loading }] = graphql.useUnsignEvaluationSubmissionMutation();

	if (
		!roles.isWorkspaceSuperuser ||
		(props.submission.employee.status !== graphql.EvaluationSubmissionStatus.Signed &&
			props.submission.manager.status !== graphql.EvaluationSubmissionStatus.Signed)
	) {
		return null;
	}

	const canUnsignEmployee =
		props.submission.employee.status === graphql.EvaluationSubmissionStatus.Signed &&
		(props.submission.employee.requiresSignature || !props.submission.manager.requiresSignature);
	const canUnsignManager =
		props.submission.manager.status === graphql.EvaluationSubmissionStatus.Signed &&
		(props.submission.manager.requiresSignature || !props.submission.employee.requiresSignature);

	return (
		<Card p={`${tokens.padding.container}px`} mb={4} chevron={false}>
			<FlexGap flexDirection="column" gap={tokens.gap.list}>
				<FlexGap flexDirection="column" gap={tokens.gap.inline}>
					<Heading3>{t('evaluation.submission.undo.title')}</Heading3>
					<Paragraph>{t('evaluation.submission.undo.description')}</Paragraph>
				</FlexGap>
				{canUnsignEmployee && (
					<FlexGap flexDirection="column" alignItems="flex-start" gap={tokens.gap.list}>
						<ConversationParty
							result={props.submission.employee as graphql.EvaluationSubmissionResult}
						/>
						<Button
							onClick={() => {
								setLoadingType('employee');
								unsign({
									variables: {
										input: {
											id: props.submission.id,
											party: graphql.EvaluationSubmissionParty.Employee,
										},
									},
								});
							}}
							title={t('evaluation.submission.undo.employee.button.title')}
							small
							loading={loading && loadingType === 'employee'}
							type="destructive"
						/>
					</FlexGap>
				)}
				{canUnsignManager && (
					<FlexGap flexDirection="column" alignItems="flex-start" gap={tokens.gap.list}>
						<ConversationParty
							result={props.submission.manager as graphql.EvaluationSubmissionResult}
						/>
						<Button
							onClick={() => {
								setLoadingType('manager');
								unsign({
									variables: {
										input: {
											id: props.submission.id,
											party: graphql.EvaluationSubmissionParty.Manager,
										},
									},
								});
							}}
							title={t('evaluation.submission.undo.manager.button.title')}
							small
							loading={loading && loadingType === 'manager'}
							type="destructive"
						/>
					</FlexGap>
				)}
			</FlexGap>
		</Card>
	);
}
